// The designed loading / error states for the task surfaces (never a blank surface). The
// 403 branch mirrors the server's band:read forbidden. The empty state ("No tasks yet") is rendered by the
// card itself via <EmptyState>, so its copy stays with the surface that shows it.
import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import styles from "./TasksCard.module.scss";

/** Centered spinner while a task surface loads. */
export function TasksLoading({ label = "Loading tasks…" }: { label?: string }) {
  return (
    <div className={styles.center} role="status" aria-live="polite">
      <Spinner size={22} label={label} />
      <p className={styles.centerText}>{label}</p>
    </div>
  );
}

/** band:read / insufficient-capability state (server 403). File-local — reached via {@link TasksError}. */
function TasksForbidden() {
  return (
    <EmptyState
      title="Tasks unavailable"
      description="Your role on this band doesn't include the task board."
    />
  );
}

/**
 * The right state for a failed task query: 403 → the forbidden state (the client mirror of the server 403);
 * anything else → the generic error with the server detail verbatim + a retry.
 */
export function TasksError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) return <TasksForbidden />;
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return (
    <EmptyState
      title="Couldn't load tasks"
      description={message}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}
